/**
 * Category Variant Manager Module
 * Initializes one scoped ProductVariantManager per product card wherever variant
 * cards exist - category pages and CMS pages with ProductParts alike - enabling
 * variant selection and add-to-cart directly from product listings.
 *
 * Each card carries its own variant data in window.categoryVariantData[productId],
 * rendered server-side by the {{#ForEachCategoryProduct}} / {{#ForEachSelectedProduct}}
 * loops.
 */

import { ProductVariantManager } from './variants.js';

export class CategoryVariantManager {
    constructor(system) {
        this.system = system;
        this.cardManagers = [];
    }

    init() {
        // Feature-detect per card instead of a page-type gate: initialize wherever
        // variant cards exist in the DOM - category pages and CMS pages with
        // ProductParts alike. The old window.templateType === 'Category' gate
        // silently disabled variant selection on CMS pages.
        const cards = document.querySelectorAll('.section-cart-product .product-info');
        if (cards.length === 0) {
            console.log('CategoryVariantManager: No product cards found, skipping initialization');
            return;
        }

        cards.forEach(card => {
            // The card must actually carry the selector markup - variant data
            // alone is not enough to build a working selector
            if (!card.querySelector('.js-variant-selectors')) {
                return;
            }

            const productId = card.querySelector('.js-cart-productid')?.textContent?.trim();
            const variantData = productId && window.categoryVariantData
                ? window.categoryVariantData[productId]
                : null;

            // Simple products without variants need no variant handling -
            // their static price/stock is rendered server-side
            if (!variantData || variantData.hasVariants !== true) {
                return;
            }

            const manager = new ProductVariantManager(this.system, {
                root: card,
                mode: 'card',
                variantData: variantData
            });
            manager.init();
            this.cardManagers.push(manager);
        });

        console.log('CategoryVariantManager: Initialized', this.cardManagers.length, 'variant card(s)');
    }

    /**
     * Get the scoped variant manager for a specific product card
     * @param {string} productId - Product ID (GUID)
     * @returns {ProductVariantManager|null}
     */
    getCardManager(productId) {
        return this.cardManagers.find(m => m.variantData && m.root.querySelector('.js-cart-productid')?.textContent?.trim() === productId) || null;
    }

    isInitialized() {
        return this.cardManagers.length > 0;
    }
}

// Export for global access
export default CategoryVariantManager;
